import type { PageDesign, PageFont, PageIcon } from "./page-design";

// The runtime half of page design (see page-design.ts): how a page's design, fonts, colors and
// icons become styles, with no schema library, so a creator site's script stays small.

export function isPageColor(value: string | undefined): value is string {
  return value !== undefined && /^#[0-9a-fA-F]{6}$/.test(value);
}

/** Attributes for the page root that select design treatments in React and Astro alike. */
export function pageDesignAttributes(design: PageDesign | undefined) {
  return {
    "data-page-typography": design?.themeTypography ? "theme" : undefined,
    // The site's styles hide its header and footer around a standalone page (site-layout.css).
    "data-page-frame": design?.frame === "standalone" ? "standalone" : undefined,
  };
}

const fonts: Record<Exclude<PageFont, `upload-${string}`>, string> = {
  theme: "var(--display, inherit)",
  // A site's own body font when its appearance sets one (shared/src/site-appearance.ts).
  body: "var(--creator-body-font, InterVariable, Inter, ui-sans-serif, system-ui, sans-serif)",
  // The theme's third face; the headings' when it has none.
  accent: "var(--creator-accent-font, var(--display, inherit))",
  condensed: "DrateCondensed, Impact, sans-serif",
  serif: "Georgia, serif",
  mono: "ui-monospace, SFMono-Regular, monospace",
};
export function pageFont(font: PageFont | undefined, fallback = "inherit") {
  if (!font) return fallback;
  // An uploaded font is declared under its id's family (shared/src/editor-v2/fonts.ts).
  const upload = /^upload-([a-z0-9]{6,16})$/.exec(font)?.[1];
  if (upload) return `"DrateFont-${upload}", var(--creator-body-font, ui-sans-serif, sans-serif)`;
  return fonts[font as keyof typeof fonts] ?? fallback;
}

export function pageDesignStyle(design: PageDesign | undefined): Record<string, string> {
  return {
    "--page-root-width":
      design?.width === "full" ? "100%" : `min(100%, ${design?.maxWidth ?? 1200}px)`,
    "--page-root-padding":
      design?.padding === undefined ? "clamp(24px, 6vw, 72px)" : `${design.padding}px`,
    "--page-root-gap": `${design?.gap ?? 32}px`,
    "--page-heading-font": pageFont(design?.headingFont, "var(--display, inherit)"),
  };
}

/** Only these trusted paths can become decorative masks; authored SVG is never accepted. */
const iconPaths: Record<PageIcon, string> = {
  "arrow-right": "M5 12h14 M12 5l7 7-7 7",
  swords:
    "M14.5 17.5L3 6V3h3l11.5 14.5 M13 19l6-6 M16 16l4 4 M19 19l2 2 M14.5 6.5L18 3h3v3l-3.5 3.5 M5 14l4 4 M7 17l-3 3 M3 19l2 2",
  radio:
    "M16.25 7.76a6 6 0 0 1 0 8.48 M19.07 4.93a10 10 0 0 1 0 14.14 M4.93 19.07a10 10 0 0 1 0-14.14 M7.75 16.24a6 6 0 0 1 0-8.48 M14 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0",
  users:
    "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M22 21v-2a4 4 0 0 0-3-3.87 M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0 M16 3.13a4 4 0 0 1 0 7.75",
  crosshair: "M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0 M22 12h-4 M6 12H2 M12 6V2 M12 22v-4",
  gauge: "M12 14l4-4 M3.34 19a10 10 0 1 1 17.32 0",
  shield: "M12 22s8-4 8-11V5l-8-3-8 3v6c0 7 8 11 8 11",
  wind: "M2 8h9a2 2 0 1 0-2-2 M2 12h17a2.5 2.5 0 1 0-2-4 M2 16h12a2 2 0 1 1-2 2",
  book: "M12 5v16 M22 17V5a2 2 0 0 0-2-2h-4a5 5 0 0 0-4 2 5 5 0 0 0-4-2H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4a5 5 0 0 1 4 2 5 5 0 0 1 4-2h4a2 2 0 0 0 2-2",
  // Drawn by the stylesheet as a filled circle; the mask is unused.
  dot: "",
  sparkles:
    "M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z M20 2v4 M22 4h-4 M2 20a2 2 0 1 0 4 0 2 2 0 1 0-4 0",
  "external-link": "M15 3h6v6 M10 14 21 3 M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6",
  "arrow-up-right": "M7 7h10v10 M7 17 17 7",
  tv: "M17 2l-5 5-5-5 M4 7h16a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z",
  play: "M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z",
  "message-circle":
    "M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719",
  trophy:
    "M10 14.66v1.626a2 2 0 0 1-.976 1.696A5 5 0 0 0 7 21.978 M14 14.66v1.626a2 2 0 0 0 .976 1.696A5 5 0 0 1 17 21.978 M18 9h1.5a1 1 0 0 0 0-5H18 M4 22h16 M6 9a6 6 0 0 0 12 0V3a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1z M6 9H4.5a1 1 0 0 1 0-5H6",
  gamepad:
    "M6 11h4 M8 9v4 M15 12h.01 M18 10h.01 M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.545-.604-6.584-.685-7.258-.007-.05-.011-.1-.017-.151A4 4 0 0 0 17.32 5z",
  camera:
    "M13.997 4a2 2 0 0 1 1.76 1.05l.486.9A2 2 0 0 0 18.003 7H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.997a2 2 0 0 0 1.759-1.048l.489-.904A2 2 0 0 1 10.004 4z M9 13a3 3 0 1 0 6 0 3 3 0 1 0-6 0",
  image:
    "M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z M7 9a2 2 0 1 0 4 0 2 2 0 1 0-4 0 M21 15l-3.086-3.086a2 2 0 0 0-2.828 0L6 21",
  "shopping-bag": "M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z M3 6h18 M16 10a4 4 0 0 1-8 0",
  apple:
    "M12 20.94c1.5 0 2.75 1.06 4 1.06 3 0 6-8 6-12.22A4.91 4.91 0 0 0 17 5c-2.22 0-4 1.44-5 2-1-.56-2.78-2-5-2a4.9 4.9 0 0 0-5 4.78C2 14 5 22 8 22c1.25 0 2.5-1.06 4-1.06Z M10 2c1 .5 2 2 2 5",
};
export function pageIconMask(icon: PageIcon | undefined) {
  if (!icon || icon === "dot") return "none";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="${iconPaths[icon]}"/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
